import { SelectedFilters } from '@lidea/shared/data-access/core';

import {
  SyncJob,
  SYNC_JOB_VERSION,
  CHUNK_SIZE,
  SYNC_CONCURRENCY,
  HEATMAP_PLOT_THRESHOLD,
} from './types';
import { isWideOfflineRole } from './wide-roles';

export type CreateSyncJobParams = {
  campaignId: number;
  userRole: string;
  managerId?: number;
  managerLabel?: string;
  /** When set, overrides role-based default for satellite / heatmap download. */
  includeHeatmaps?: boolean;
};

export function buildSyncFilters(
  managerId?: number
): SelectedFilters {
  const filters: SelectedFilters = { search_query: '' };
  if (managerId != null) {
    filters.manager = [String(managerId)];
  }
  return filters;
}

/** Heatmaps for field roles; wide roles only when a CDS scope (managerId) is set. */
export function shouldIncludeHeatmapsAtCreate(
  userRole: string,
  managerId?: number
): boolean {
  if (managerId != null) {
    return true;
  }
  return !isWideOfflineRole(userRole);
}

export function applyHeatmapThreshold(job: SyncJob): SyncJob {
  if (
    job.totals.plots > HEATMAP_PLOT_THRESHOLD &&
    job.options.includeHeatmaps
  ) {
    return {
      ...job,
      options: { ...job.options, includeHeatmaps: false },
      pendingHeatmapTasks: [],
      totals: { ...job.totals, heatmapTasks: 0 },
    };
  }
  return job;
}

function newSyncJobId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sync-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function createSyncJob(params: CreateSyncJobParams): SyncJob {
  const now = Date.now();
  const filters = buildSyncFilters(params.managerId);

  return {
    id: newSyncJobId(),
    version: SYNC_JOB_VERSION,
    status: 'listing',
    createdAt: now,
    updatedAt: now,
    scope: {
      campaignId: params.campaignId,
      managerId: params.managerId,
      managerLabel: params.managerLabel,
      filters,
      userRole: params.userRole,
    },
    plotIds: [],
    pendingPlotIds: [],
    pendingAnalysisIds: [],
    imageryIndexes: [],
    pendingHeatmapTasks: [],
    totals: { plots: 0, analyses: 0, heatmapTasks: 0 },
    completedPlots: 0,
    completedAnalyses: 0,
    completedHeatmapTasks: 0,
    options: {
      includeHeatmaps:
        params.includeHeatmaps ??
        shouldIncludeHeatmapsAtCreate(params.userRole, params.managerId),
      chunkSize: CHUNK_SIZE,
      concurrency: SYNC_CONCURRENCY,
    },
    errors: [],
  };
}
